import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getResource } from '../../api/resource';
import { getCountByStatus } from '../../api/project';
import Sidebar from '../../components/sidebar';
import { ReactComponent as SearchIcon } from '../../ui/assets/star.svg';

const getStatusEnum = (status) => {
  switch (status) {
    case 'draft':
      return 'DRAFT';
    case 'submitted':
      return 'SUBMITTED';
    case 'inProgress':
      return 'IN_PROGRESS';
    case 'completed':
      return 'COMPLETED';
    default:
      return status;
  }
};

export default function ProjectOverviewDesigner() {
  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const { getAccessTokenSilently } = useAuth0();
  const [tabCount, setTabCount] = useState({
    all: 0,
    draft: 0,
    submitted: 0,
    inProgress: 0,
    completed: 0,
  });

  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchProjects = async (
    page = currentPage - 1,
    size = itemsPerPage,
    status = activeTab,
    search = searchTerm,
  ) => {
    try {
      const token = await getAccessTokenSilently();

      let queryParams = `?page=${page}&size=${size}&sort=created,desc`;

      const path = status === 'all' ? '' : '/findByStatus';

      if (search) {
        queryParams += `&search=${encodeURIComponent(search)}`;
      }
      if (status !== 'all') {
        queryParams += `&status=${getStatusEnum(status)}`;
      }

      const projectResource = await getResource(
        token,
        `${process.env.REACT_APP_BACKEND_URL}/rest/projects${path}${queryParams}`,
      );
      if (!projectResource || !projectResource.content) {
        setProjects([]);
        setTotalPages(0);
        return;
      }
      setTotalPages(projectResource.totalPages);

      const projectsWithRooms = await Promise.all(
        projectResource.content.map(async (project) => {
          return {
            ...project,
          };
        }),
      );
      setProjects(projectsWithRooms);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setProjects([]);
      setTotalPages(0);
    }
  };

  const handleClickDetails = (project) => {
    navigate(`/project/${project.id}`);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    fetchProjects(0, itemsPerPage, tab, searchTerm);
  };

  const handleSearchChange = (event) => {
    clearTimeout(window.searchTimeout);
    const searchValue = event.target.value;
    setSearchTerm(searchValue);

    window.searchTimeout = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
      fetchProjects(0, itemsPerPage, activeTab, searchValue);
    }, 500); // Debounce for 500ms
  };

  const fetchTabCounts = async () => {
    try {
      const token = await getAccessTokenSilently();
      const statuses = ['DRAFT', 'SUBMITTED', 'IN_PROGRESS', 'COMPLETED'];
      const counts = await Promise.all(
        statuses.map(async (status) => {
          const count = await getCountByStatus(token, status);
          if (!count) {
            return 0;
          }
          return parseInt(count, 10);
        }),
      );
      setTabCount({
        all: counts.reduce((acc, curr) => acc + curr, 0),
        draft: counts[0],
        submitted: counts[1],
        inProgress: counts[2],
        completed: counts[3],
      });
    } catch (error) {
      console.error('Failed to fetch tab counts:', error);
      setTabCount({
        all: 0,
        draft: 0,
        submitted: 0,
        inProgress: 0,
        completed: 0,
      });
    }
  };

  useEffect(() => {
    fetchTabCounts();
    fetchProjects();
  }, []);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchProjects(pageNumber - 1, itemsPerPage, activeTab, searchTerm);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return dateString; // Placeholder
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'project-overview-designer__table-cell--status-draft';
      case 'SUBMITTED':
        return 'project-overview-designer__table-cell--status-submitted';
      case 'IN_PROGRESS':
        return 'project-overview-designer__table-cell--status-inprogress';
      case 'COMPLETED':
        return 'project-overview-designer__table-cell--status-completed';
      default:
        return '';
    }
  };

  const getFormattedStatus = (status) => {
    switch (status) {
      case 'DRAFT':
        return t('pages.projectOverviewDesigner.status.draft');
      case 'SUBMITTED':
        return t('pages.projectOverviewDesigner.status.submitted');
      case 'IN_PROGRESS':
        return t('pages.projectOverviewDesigner.status.inProgress');
      case 'COMPLETED':
        return t('pages.projectOverviewDesigner.status.completed');
      default:
        return status;
    }
  };

  const getRoomType = (rooms) => {
    if (!rooms || rooms.length === 0) return '-';
    return rooms[0]?.type || '-';
  };

  const getClientName = (project) => {
    if (!project.client) return '-';
    return `${project.client.firstName || ''} ${project.client.lastName || ''}`.trim() || '-';
  };

  return (
    <div className="page-with-sidebar">
      <Sidebar />
      <div className="layout-container">
        <div className="project-overview-designer">
          <h3>{t('pages.projectOverviewDesigner.title')}</h3>
          <div className="project-overview-designer__subtitle">
            {t('pages.projectOverviewDesigner.subtitle')}
          </div>
          <div className="project-overview-designer__content">
            <div className="project-overview-designer__header">
              <div className="project-overview-designer__tab-container">
                <button
                  type="button"
                  className={`project-overview-designer__tab ${
                    activeTab === 'all' ? 'project-overview-designer__tab--active' : ''
                  }`}
                  onClick={() => handleTabChange('all')}
                >
                  {t('pages.projectOverviewDesigner.tabs.all')}
                  <span className="project-overview-designer__tab-count">{tabCount?.all}</span>
                </button>
                <button
                  type="button"
                  className={`project-overview-designer__tab ${
                    activeTab === 'draft' ? 'project-overview-designer__tab--active' : ''
                  }`}
                  onClick={() => handleTabChange('draft')}
                >
                  {t('pages.projectOverviewDesigner.tabs.draft')}
                  <span className="project-overview-designer__tab-count">{tabCount?.draft}</span>
                </button>
                <button
                  type="button"
                  className={`project-overview-designer__tab ${
                    activeTab === 'submitted' ? 'project-overview-designer__tab--active' : ''
                  }`}
                  onClick={() => handleTabChange('submitted')}
                >
                  {t('pages.projectOverviewDesigner.tabs.submitted')}
                  <span className="project-overview-designer__tab-count">
                    {tabCount?.submitted}
                  </span>
                </button>
                <button
                  type="button"
                  className={`project-overview-designer__tab ${
                    activeTab === 'inProgress' ? 'project-overview-designer__tab--active' : ''
                  }`}
                  onClick={() => handleTabChange('inProgress')}
                >
                  {t('pages.projectOverviewDesigner.tabs.inProgress')}
                  <span className="project-overview-designer__tab-count">
                    {tabCount?.inProgress}
                  </span>
                </button>
                <button
                  type="button"
                  className={`project-overview-designer__tab ${
                    activeTab === 'completed' ? 'project-overview-designer__tab--active' : ''
                  }`}
                  onClick={() => handleTabChange('completed')}
                >
                  {t('pages.projectOverviewDesigner.tabs.completed')}
                  <span className="project-overview-designer__tab-count">
                    {tabCount?.completed}
                  </span>
                </button>
              </div>

              <div className="project-overview-designer__search">
                <SearchIcon className="project-overview-designer__search-icon" />
                <input
                  type="text"
                  className="project-overview-designer__search-input"
                  placeholder={t('pages.projectOverviewDesigner.searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            <div className="project-overview-designer__table-container">
              <table className="project-overview-designer__table">
                <thead>
                  <tr>
                    <th>{t('pages.projectOverviewDesigner.table.assignedTo')}</th>
                    <th>{t('pages.projectOverviewDesigner.table.projectId')}</th>
                    <th>{t('pages.projectOverviewDesigner.table.dateCreated')}</th>
                    <th>{t('pages.projectOverviewDesigner.table.deadline')}</th>
                    <th>{t('pages.projectOverviewDesigner.table.room')}</th>
                    <th>{t('pages.projectOverviewDesigner.table.progress')}</th>
                    <th>{t('pages.projectOverviewDesigner.table.status')}</th>
                    <th>{}</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <tr
                      key={project.id}
                      className="project-overview-designer__table-row"
                      onClick={() => handleClickDetails(project)}
                    >
                      <td className="project-overview-designer__table-cell">
                        <div className="project-overview-designer__client-info">
                          <div className="project-overview-designer__client-avatar" />
                          <span>{getClientName(project)}</span>
                        </div>
                      </td>
                      <td className="project-overview-designer__table-cell">
                        {project.name || '-'}
                      </td>
                      <td className="project-overview-designer__table-cell">
                        {formatDate(project.created)}
                      </td>
                      <td className="project-overview-designer__table-cell">
                        {formatDate(project.deadline)}
                      </td>
                      <td className="project-overview-designer__table-cell">
                        {getRoomType(project.rooms)}
                      </td>
                      <td
                        className="project-overview-designer__table-cell"
                        aria-label={t('pages.projectOverviewDesigner.table.progress')}
                      >
                        <div className="project-overview-designer__progress-bar">
                          <div
                            className={`project-overview-designer__progress-fill project-overview-designer__progress-fill--${project.status?.toLowerCase()}`}
                            style={{ width: `${project.progress || 0}%` }}
                          />
                        </div>
                      </td>
                      <td
                        className={`project-overview-designer__table-cell ${getStatusClass(
                          project.status,
                        )}`}
                      >
                        {getFormattedStatus(project.status)}
                      </td>
                      <td className="project-overview-designer__table-cell">
                        <button type="button" className="project-overview-designer__details-btn">
                          {t('pages.projectOverviewDesigner.buttons.details')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="project-overview-designer__pagination">
                <span className="project-overview-designer__pagination-info">
                  {t('pages.projectOverviewDesigner.pagination.itemsPerPage', {
                    count: itemsPerPage,
                  })}
                </span>
                <div className="project-overview-designer__pagination-controls">
                  <button
                    type="button"
                    className="project-overview-designer__pagination-btn"
                    onClick={() => paginate(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    &lt;
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                    let pageNumber;
                    if (totalPages <= 5) {
                      pageNumber = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + idx;
                    } else {
                      pageNumber = currentPage - 2 + idx;
                    }

                    if (pageNumber > 0 && pageNumber <= totalPages) {
                      return (
                        <button
                          type="button"
                          key={pageNumber}
                          className={`project-overview-designer__pagination-btn ${
                            currentPage === pageNumber
                              ? 'project-overview-designer__pagination-btn--active'
                              : ''
                          }`}
                          onClick={() => paginate(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button
                    type="button"
                    className="project-overview-designer__pagination-btn"
                    onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
